<template>
  <aside ref="overlay" class="dashboard-base-sidebar-ui" tabindex="0">
    <div class="dashboard-base-sidebar-ui__content">
      <slot />
    </div>
    <div class="dashboard-base-sidebar-ui__close-btn">
      <CloseBtn ref="closeBtn" class="sidebar" @click="close" />
    </div>
  </aside>
</template>

<script setup lang="ts">
  // Services
  import { KeyboardService } from '~/misc/services/keyboard';
  import { FocusService } from '~/misc/services/focus';

  // Components
  import CloseBtn from '~/misc/btn/close/CloseBtn.vue';

  const keyboardService = ref(new KeyboardService());

  const props = defineProps({
    isSidebarShown: {
      type: Boolean,
      required: true,
    },
  });

  const emit = defineEmits<{
    (e: 'close'): void;
  }>();

  const overlay: Ref<HTMLElement | null> = ref<HTMLElement | null>(null);

  const closeBtn: Ref<InstanceType<typeof CloseBtn> | null> = ref<InstanceType<
    typeof CloseBtn
  > | null>(null);

  const close = () => {
    emitClose();
    keyboardService.value.destroy();
  };

  const initKeyboardService = () => {
    const overlayEl = overlay.value as HTMLElement;
    const closeBtnEl = closeBtn.value?.$el as HTMLButtonElement;

    const focusService: FocusService = new FocusService(overlayEl, closeBtnEl);

    if (!overlay || !closeBtn) {
      return;
    }

    keyboardService.value.init(focusService);
    keyboardService.value.onEscKeyUp((event: KeyboardEvent) => {
      const target = event.target as HTMLElement;

      if (
        !target ||
        target === overlayEl ||
        target.closest<HTMLDivElement>('.dashboard-base-sidebar-ui') === overlayEl
      ) {
        close();
      }
    });
  };

  const emitClose = () => emit('close');

  watch(
    () => props.isSidebarShown,
    async (value: boolean) => {
      if (value) {
        await nextTick();

        initKeyboardService();
      }
    }
  );
</script>
